/**
 * Onboarding State Machine - SINGLE SOURCE OF TRUTH
 * 
 * This module is THE ONLY authority for onboarding flow control.
 * ALL components MUST consult this state machine before rendering or routing.
 * 
 * CANONICAL SEQUENCE (native iOS):
 * A. PRE-AUTH (no login yet):
 *   1. 'pre_auth_welcome' - 4-box welcome screen (native only, first run)
 *   2. 'pre_auth_typewriter' - Typewriter intro (native only)
 * B. AUTH:
 *   3. 'auth' - Sign in / Sign up
 * C. POST-AUTH (inside the app):
 *   4. 'conversational' - Conversational onboarding (tone, goals, support style)
 *   5. 'tour' - 14-step guided tour (auto-starts after conversational)
 *   6. 'app' - Daily Habits Dashboard
 * 
 * CRITICAL SAFETY RULE:
 * If user is authenticated (_userId is set), NEVER show pre-auth stages.
 * This is enforced by checking _userId FIRST in computeStage().
 * 
 * DB STORAGE (profiles table):
 * - onboarding_completed: boolean (default false) - CANONICAL for post-auth flow
 * 
 * LOCAL STORAGE (per-device):
 * - therappi_pre_auth_welcome_seen: shown 4-box welcome (pre-auth gate)
 * - therappi_pre_auth_typewriter_seen: shown typewriter intro (pre-auth gate)
 * - therappi_tour_completed: completed guided tour
 * 
 * CRITICAL RULES:
 * - NO component may route/redirect independently
 * - NO component may render onboarding UI without checking this state
 * - State transitions happen ONLY through this module's functions
 * - profiles.onboarding_completed is the CANONICAL source for post-auth flow
 * - Authenticated users go DIRECTLY to post-auth flow (conversational/tour/app)
 */

import { Capacitor } from '@capacitor/core';
import { setOnboardingCompletedDB } from '@/hooks/useTourController';

// ============= TYPES =============
export type OnboardingStage = 
  | 'hydrating'           // Loading auth/profile, no decisions yet
  | 'pre_auth_welcome'    // Stage 1: 4-box welcome (native only)
  | 'pre_auth_typewriter' // Stage 2: Typewriter intro (native only)
  | 'auth'                // Stage 3: Sign in / Sign up
  | 'conversational'      // Stage 4: Conversational onboarding
  | 'tour'                // Stage 5: Guided tour
  | 'app';                // Stage 6: Main app

// Backward compatibility - old state names mapped to new
export type OnboardingState = 
  | 'hydrating'
  | 'pre_auth'       // Maps to pre_auth_welcome
  | 'auth'
  | 'onboarding'     // Maps to conversational
  | 'welcome'        // No longer used - kept for compat, maps to app
  | 'tour'
  | 'app';

// ============= STORAGE KEYS =============
// CANONICAL keys (underscore format)
const KEYS = {
  PRE_AUTH_WELCOME: 'therappi_pre_auth_welcome_seen',
  PRE_AUTH_TYPEWRITER: 'therappi_pre_auth_typewriter_seen',
  TOUR_COMPLETED: 'therappi_tour_completed',
} as const;

// LEGACY keys (hyphen format) - for migration/cleanup
const LEGACY_KEYS = {
  PRE_AUTH_WELCOME: 'therappi-pre-auth-welcome-seen',
  PRE_AUTH_TYPEWRITER: 'therappi-pre-auth-typewriter-seen',
} as const;

// ============= FLIGHT RECORDER =============
interface FlightLogEntry {
  timestamp: string;
  event: string;
  data: Record<string, unknown>;
}

const flightLog: FlightLogEntry[] = [];
const MAX_LOG_SIZE = 200;

export function logFlight(event: string, data: Record<string, unknown> = {}) {
  const entry: FlightLogEntry = {
    timestamp: new Date().toISOString(),
    event,
    data: {
      ...data,
      currentStage: _currentStage,
      isNative: Capacitor.isNativePlatform(),
    }
  };
  
  flightLog.push(entry);
  if (flightLog.length > MAX_LOG_SIZE) {
    flightLog.shift();
  }
  
  console.log(`[FLIGHT] ${event}`, entry.data);
}

export function getFlightLog(): FlightLogEntry[] {
  return [...flightLog];
}

export function clearFlightLog() {
  flightLog.length = 0;
}

// ============= MODULE-LEVEL STATE =============
let _currentStage: OnboardingStage = 'hydrating';
let _authHydrated = false;
let _profileHydrated = false;
let _userId: string | null = null;
let _onboardingCompletedDB: boolean | null = null; // From DB
let _tourStep = 0;
let _listeners: Set<() => void> = new Set();

// Window flag for external checks
declare global {
  interface Window {
    __TOUR_ACTIVE__?: boolean;
    __ONBOARDING_STAGE__?: OnboardingStage;
  }
}

function syncWindowFlags() {
  try {
    window.__TOUR_ACTIVE__ = _currentStage === 'tour';
    window.__ONBOARDING_STAGE__ = _currentStage;
  } catch {
    // SSR
  }
}

// ============= LOCAL STORAGE HELPERS =============

/**
 * Get flag with migration: checks both canonical AND legacy keys
 * Returns true if EITHER key variant is set
 */
function getFlag(key: string): boolean {
  try {
    // Check canonical key first
    if (localStorage.getItem(key) === 'true') {
      return true;
    }
    // Check legacy key for pre-auth flags (migration)
    if (key === KEYS.PRE_AUTH_WELCOME && localStorage.getItem(LEGACY_KEYS.PRE_AUTH_WELCOME) === 'true') {
      return true;
    }
    if (key === KEYS.PRE_AUTH_TYPEWRITER && localStorage.getItem(LEGACY_KEYS.PRE_AUTH_TYPEWRITER) === 'true') {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Set flag: writes to canonical key AND clears legacy key
 */
function setFlag(key: string, value: boolean): void {
  try {
    if (value) {
      localStorage.setItem(key, 'true');
      // Also clear legacy variant to prevent confusion
      if (key === KEYS.PRE_AUTH_WELCOME) {
        localStorage.removeItem(LEGACY_KEYS.PRE_AUTH_WELCOME);
      }
      if (key === KEYS.PRE_AUTH_TYPEWRITER) {
        localStorage.removeItem(LEGACY_KEYS.PRE_AUTH_TYPEWRITER);
      }
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore
  }
}

/**
 * Clear all flags: removes BOTH canonical AND legacy keys
 */
function clearAllFlags(): void {
  try {
    // Clear canonical keys
    Object.values(KEYS).forEach(key => localStorage.removeItem(key));
    // Clear legacy keys
    Object.values(LEGACY_KEYS).forEach(key => localStorage.removeItem(key));
    logFlight('CLEAR_ALL_FLAGS', { cleared: [...Object.values(KEYS), ...Object.values(LEGACY_KEYS)] });
  } catch {
    // Ignore
  }
}

// ============= STATE GETTERS =============
export function getOnboardingStage(): OnboardingStage {
  return _currentStage;
}

// Backward compatibility alias
export function getOnboardingState(): OnboardingStage {
  return _currentStage;
}

export function isHydrated(): boolean {
  return _authHydrated && _profileHydrated;
}

export function isAuthHydrated(): boolean {
  return _authHydrated;
}

export function isProfileHydrated(): boolean {
  return _profileHydrated;
}

export function getTourStep(): number {
  return _tourStep;
}

export function getUserId(): string | null {
  return _userId;
}

export function hasUser(): boolean {
  return _userId !== null;
}

export function isTourActive(): boolean {
  return _currentStage === 'tour';
}

export function isInOnboardingFlow(): boolean {
  return ['pre_auth_welcome', 'pre_auth_typewriter', 'conversational', 'tour'].includes(_currentStage);
}

// ============= STAGE COMPUTATION =============
function computeStage(): OnboardingStage {
  const isNative = Capacitor.isNativePlatform();
  
  // Priority 1: Not hydrated
  if (!_authHydrated || !_profileHydrated) {
    return 'hydrating';
  }
  
  // CRITICAL SAFETY: If user is authenticated, NEVER show pre-auth stages
  // This is checked FIRST to prevent any race conditions after auth
  if (_userId) {
    // Priority 4: User exists - check post-auth flow
    // CANONICAL: profiles.onboarding_completed is THE source of truth
    
    // Stage 4: Conversational onboarding (if DB says not completed)
    if (_onboardingCompletedDB !== true) {
      return 'conversational';
    }
    
    // Stage 5: Tour (if onboarding done but tour not done)
    if (!getFlag(KEYS.TOUR_COMPLETED)) {
      return 'tour';
    }
    
    // Stage 6: All complete
    return 'app';
  }
  
  // No user - check pre-auth flow (native only)
  // Priority 2: Native pre-auth flow
  // Stage 1: 4-box welcome (native only, first run, NO user)
  if (isNative && !getFlag(KEYS.PRE_AUTH_WELCOME)) {
    return 'pre_auth_welcome';
  }
  
  // Stage 2: Typewriter intro (native only, after 4-box, NO user)
  if (isNative && !getFlag(KEYS.PRE_AUTH_TYPEWRITER)) {
    return 'pre_auth_typewriter';
  }
  
  // Priority 3: No user - need auth
  return 'auth';
}

function recomputeStage(trigger: string): void {
  const prevStage = _currentStage;
  const newStage = computeStage();
  
  if (prevStage !== newStage) {
    _currentStage = newStage;
    syncWindowFlags();
    logFlight('STAGE_CHANGE', {
      trigger,
      from: prevStage,
      to: newStage,
      userId: _userId,
      onboardingCompletedDB: _onboardingCompletedDB,
      flags: {
        preAuthWelcome: getFlag(KEYS.PRE_AUTH_WELCOME),
        tourCompleted: getFlag(KEYS.TOUR_COMPLETED),
      }
    });
    
    // Notify listeners
    _listeners.forEach(listener => listener());
  }
}

// ============= STATE SETTERS (internal use) =============
export function setAuthHydrated(userId: string | null): void {
  const wasHydrated = _authHydrated;
  _authHydrated = true;
  _userId = userId;
  
  logFlight('AUTH_HYDRATED', { userId, wasHydrated });
  recomputeStage('auth_hydrated');
}

export function setProfileHydrated(onboardingCompleted: boolean | null): void {
  const wasHydrated = _profileHydrated;
  _profileHydrated = true;
  // CRITICAL: null or false = not completed
  _onboardingCompletedDB = onboardingCompleted === true ? true : false;
  setOnboardingCompletedDB(_onboardingCompletedDB);
  
  logFlight('PROFILE_HYDRATED', { 
    onboardingCompleted, 
    wasHydrated,
    resolvedValue: _onboardingCompletedDB
  });
  recomputeStage('profile_hydrated');
}

export function setTourStep(step: number): void {
  const prevStep = _tourStep;
  _tourStep = step;
  if (prevStep !== step) {
    logFlight('TOUR_STEP', { from: prevStep, to: step });
  }
}

// ============= STAGE COMPLETION FUNCTIONS =============

/**
 * Call when user completes the 4-box welcome (Stage 1)
 */
export function completePreAuthWelcome(): void {
  setFlag(KEYS.PRE_AUTH_WELCOME, true);
  logFlight('COMPLETE_PRE_AUTH_WELCOME', {});
  recomputeStage('complete_pre_auth_welcome');
}

/**
 * Call when user completes the typewriter intro (Stage 2)
 */
export function completePreAuthTypewriter(): void {
  setFlag(KEYS.PRE_AUTH_TYPEWRITER, true);
  logFlight('COMPLETE_PRE_AUTH_TYPEWRITER', {});
  recomputeStage('complete_pre_auth_typewriter');
}

/**
 * Call when conversational onboarding completes and DB is updated
 * CRITICAL: This is called AFTER profiles.onboarding_completed is set to true in DB
 */
export function completeConversational(): void {
  _onboardingCompletedDB = true;
  logFlight('COMPLETE_CONVERSATIONAL', {});
  recomputeStage('complete_conversational');
}

/**
 * Call when guided tour completes (Stage 5)
 */
export function completeTour(): void {
  setFlag(KEYS.TOUR_COMPLETED, true);
  _tourStep = 0;
  logFlight('COMPLETE_TOUR', {});
  recomputeStage('complete_tour');
}

// Backward compatibility aliases
export function markOnboardingDone(): void {
  completeConversational();
}

export function markWelcomeSeen(): void {
  // This was for the old post-auth typewriter - now it's pre-auth
  logFlight('MARK_WELCOME_SEEN_DEPRECATED', {});
}

export function markTourSeen(): void {
  completeTour();
}

// ============= NAVIGATION GUARD =============
export function canNavigate(from: string, to: string, reason: string): boolean {
  // Block navigation during hydration
  if (_currentStage === 'hydrating') {
    logFlight('NAV_BLOCKED', { from, to, reason, blockReason: 'hydrating' });
    return false;
  }
  
  // During tour stage: allow navigation if tour completion flag is already set
  // (safety valve - prevents permanent navigation lock if tour dismiss was interrupted)
  if (_currentStage === 'tour') {
    if (getFlag(KEYS.TOUR_COMPLETED)) {
      // Tour flag set but stage stuck - force recompute and allow
      logFlight('NAV_TOUR_FLAG_SET_FORCING_APP', { from, to, reason });
      recomputeStage('nav_guard_tour_flag_recovery');
      return true;
    }
    if (reason !== 'tour_navigation') {
      logFlight('NAV_BLOCKED', { from, to, reason, blockReason: 'tour_active' });
      return false;
    }
  }
  
  logFlight('NAV_ALLOWED', { from, to, reason });
  return true;
}

// ============= RESET =============
export function resetOnboardingState(): void {
  logFlight('RESET_ALL', {
    prevStage: _currentStage,
    userId: _userId
  });
  
  _currentStage = 'hydrating';
  _authHydrated = false;
  _profileHydrated = false;
  _userId = null;
  _onboardingCompletedDB = null;
  _tourStep = 0;
  
  clearAllFlags();
  syncWindowFlags();
}

export function clearFirstRunFlags(): void {
  clearAllFlags();
  logFlight('CLEAR_FLAGS', {});
  recomputeStage('flags_cleared');
}

// ============= SUBSCRIPTION =============
export function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

// ============= REACT HOOK =============
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export function useOnboardingStateMachine() {
  const [stage, setStage] = useState<OnboardingStage>(_currentStage);
  const [, forceUpdate] = useState(0);
  
  // Subscribe to state changes
  useEffect(() => {
    const unsubscribe = subscribe(() => {
      setStage(_currentStage);
      forceUpdate(x => x + 1);
    });
    return unsubscribe;
  }, []);
  
  // Initial hydration
  useEffect(() => {
    if (_authHydrated) {
      logFlight('HOOK_ALREADY_HYDRATED', { userId: _userId });
      setStage(_currentStage);
      return;
    }
    
    logFlight('HOOK_HYDRATION_START', {});
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      const userId = session?.user?.id ?? null;
      setAuthHydrated(userId);
      
      if (session?.user) {
        supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('user_id', session.user.id)
          .maybeSingle()
          .then(({ data, error }) => {
            if (error) {
              console.error('[FLIGHT] Profile fetch error:', error);
              setProfileHydrated(false);
            } else {
              setProfileHydrated(data?.onboarding_completed ?? false);
            }
            setStage(_currentStage);
          });
      } else {
        setProfileHydrated(null);
        setStage(_currentStage);
      }
    });
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Block auth changes during tour
      if (_currentStage === 'tour') {
        logFlight('AUTH_CHANGE_BLOCKED', { event, reason: 'tour_active' });
        return;
      }
      
      logFlight('AUTH_CHANGE', { event, hasUser: !!session?.user });
      
      setAuthHydrated(session?.user?.id ?? null);
      
      if (session?.user) {
        supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('user_id', session.user.id)
          .maybeSingle()
          .then(({ data, error }) => {
            setProfileHydrated(error ? false : (data?.onboarding_completed ?? false));
            setStage(_currentStage);
          });
      } else {
        setProfileHydrated(null);
        setStage(_currentStage);
      }
    });
    
    return () => subscription.unsubscribe();
  }, []);
  
  const markComplete = useCallback((step: 'pre_auth_welcome' | 'pre_auth_typewriter' | 'conversational' | 'tour') => {
    switch (step) {
      case 'pre_auth_welcome':
        completePreAuthWelcome();
        break;
      case 'pre_auth_typewriter':
        completePreAuthTypewriter();
        break;
      case 'conversational':
        completeConversational();
        break;
      case 'tour':
        completeTour();
        break;
    }
    setStage(_currentStage);
  }, []);
  
  return {
    stage,
    state: stage, // backward compat
    isHydrated: isHydrated(),
    authHydrated: _authHydrated,
    profileHydrated: _profileHydrated,
    tourStep: _tourStep,
    userId: _userId,
    hasUser: _userId !== null,
    onboardingCompleted: _onboardingCompletedDB,
    canNavigate,
    markComplete,
    reset: resetOnboardingState,
  };
}

// ============= CANONICAL EXPORTS =============
// isTourActiveGlobal, isTourOrTransitioning, isOnboardingTransitioning, isConversationalCompleted
// are ALL exported from useTourController.ts (canonical source)
// Removed duplicate exports to prevent bundler conflicts

export function isTourSeen(): boolean {
  return getFlag(KEYS.TOUR_COMPLETED);
}

// For useTourController compatibility
export { setTourStep as setGlobalTourStep };
export { completeTour as markTourSeenState };
